/**
 * Web Vitals Monitoring Plugin
 *
 * Tracks Core Web Vitals (CLS, LCP, INP, FCP, TTFB) and sends data to Sentry.
 * Includes attribution data to identify which elements cause layout shifts.
 *
 * Phase 3 Performance Optimization - Real User CLS Investigation
 */

import { onCLS, onLCP, onINP, onFCP, onTTFB } from 'web-vitals/attribution';

export default defineNuxtPlugin((nuxtApp) => {
  // Only run in production or when explicitly enabled
  const isDev = process.env.NODE_ENV === 'development';

  // Function to send vitals to Sentry
  const sendToSentry = (metric) => {
    const { $sentry } = nuxtApp;

    if (!$sentry) {
      console.log('[Web Vitals]', metric.name, metric.value, metric.rating);
      return;
    }

    // Send as Sentry measurement
    $sentry.setMeasurement(metric.name, metric.value, metric.rating);

    // For CLS, include attribution data to identify shift sources
    if (metric.name === 'CLS' && metric.attribution) {
      const attribution = metric.attribution;

      // Log detailed CLS information
      console.log('[Web Vitals CLS]', {
        value: metric.value,
        rating: metric.rating,
        largestShiftTarget: attribution.largestShiftTarget,
        largestShiftValue: attribution.largestShiftValue,
        largestShiftTime: attribution.largestShiftTime,
        loadState: attribution.loadState,
      });

      // Send CLS attribution as Sentry context
      $sentry.setContext('cls_attribution', {
        value: metric.value,
        rating: metric.rating,
        largestShiftTarget: attribution.largestShiftTarget || 'unknown',
        largestShiftValue: attribution.largestShiftValue || 0,
        largestShiftTime: attribution.largestShiftTime || 0,
        loadState: attribution.loadState || 'unknown',
      });

      // If CLS is poor (>0.1), send an event to Sentry
      if (metric.value > 0.1) {
        $sentry.captureMessage('High CLS detected', {
          level: 'warning',
          extra: {
            cls_value: metric.value,
            cls_rating: metric.rating,
            largestShiftTarget: attribution.largestShiftTarget,
            largestShiftValue: attribution.largestShiftValue,
          },
        });
      }
    }

    // For LCP, include attribution data
    if (metric.name === 'LCP' && metric.attribution) {
      const attribution = metric.attribution;

      console.log('[Web Vitals LCP]', {
        value: metric.value,
        rating: metric.rating,
        element: attribution.element,
        url: attribution.url,
        timeToFirstByte: attribution.timeToFirstByte,
        resourceLoadDelay: attribution.resourceLoadDelay,
        resourceLoadTime: attribution.resourceLoadTime,
        elementRenderDelay: attribution.elementRenderDelay,
      });

      $sentry.setContext('lcp_attribution', {
        value: metric.value,
        rating: metric.rating,
        element: attribution.element?.tagName || 'unknown',
        url: attribution.url || 'unknown',
        timeToFirstByte: attribution.timeToFirstByte || 0,
        resourceLoadDelay: attribution.resourceLoadDelay || 0,
        resourceLoadTime: attribution.resourceLoadTime || 0,
        elementRenderDelay: attribution.elementRenderDelay || 0,
      });
    }
  };

  // Register Web Vitals observers
  onCLS(sendToSentry);
  onLCP(sendToSentry);
  onINP(sendToSentry);
  onFCP(sendToSentry);
  onTTFB(sendToSentry);

  console.log('[Web Vitals] Monitoring enabled');
});
